/**
 * 재사용 가능한 fetch 기반 API 클라이언트.
 *
 * - Authorization: Bearer <token> 을 localStorage 에서 읽어 자동 부착 (기존 컴포넌트마다
 *   수동 부착하던 것을 대체)
 * - 401 응답 시 로그인 화면으로 이동 (기존 fetchWithAuth 동작 유지)
 * - baseUrl 만 다른 두 백엔드를 각각 클라이언트로 노출:
 *     lmsApi   → VITE_API_URL       (lms_apis, Node)
 *     adminApi → VITE_ADMIN_API_URL (admin_v2, Laravel) — 같은 토큰, baseURL 만 다름
 *
 * 두 백엔드는 동일한 RS256 키로 토큰을 검증하므로 하나의 토큰으로 양쪽을 호출할 수 있다.
 */

const TOKEN_KEY = "token";

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) || "";
}

function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export interface ApiClient {
  baseUrl: string;
  /** 인증 헤더 부착 + 401 처리된 raw Response 반환 */
  raw(path: string, init?: RequestInit): Promise<Response>;
  /** JSON 파싱까지 수행. 응답이 ok 가 아니면 throw */
  request<T = unknown>(path: string, init?: RequestInit): Promise<T>;
  get<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T>;
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
  put<T = unknown>(path: string, body?: unknown): Promise<T>;
}

export function createApiClient(baseUrl: string | undefined): ApiClient {
  // baseUrl 미설정 시 same-origin("") → 리버스 프록시 단일 오리진 토폴로지와 호환
  const base = (baseUrl || "").replace(/\/$/, "");

  async function raw(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers || {});

    const token = getToken();
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // FormData 는 브라우저가 boundary 포함 Content-Type 을 자동 설정하도록 둔다
    const isForm =
      typeof FormData !== "undefined" && init.body instanceof FormData;
    if (!isForm && init.body !== undefined && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const res = await fetch(`${base}${path}`, { ...init, headers });

    if (res.status === 401) {
      window.location.href = "/#/signin";
      throw new Error("Unauthorized");
    }
    return res;
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await raw(path, init);
    if (!res.ok) {
      throw new Error(`API ${res.status} ${res.statusText} (${path})`);
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  return {
    baseUrl: base,
    raw,
    request,
    get<T>(path: string, params?: Record<string, unknown>) {
      return request<T>(`${path}${buildQuery(params)}`, { method: "GET" });
    },
    post<T>(path: string, body?: unknown) {
      const isForm =
        typeof FormData !== "undefined" && body instanceof FormData;
      return request<T>(path, {
        method: "POST",
        body:
          body === undefined
            ? undefined
            : isForm
              ? (body as FormData)
              : JSON.stringify(body),
      });
    },
    put<T>(path: string, body?: unknown) {
      const isForm =
        typeof FormData !== "undefined" && body instanceof FormData;
      return request<T>(path, {
        method: "PUT",
        body:
          body === undefined
            ? undefined
            : isForm
              ? (body as FormData)
              : JSON.stringify(body),
      });
    },
  };
}

/** LMS(Node) 백엔드 */
export const lmsApi = createApiClient(import.meta.env.VITE_API_URL);

/** admin(Laravel) 백엔드 — 같은 토큰, baseURL 만 다름 */
export const adminApi = createApiClient(import.meta.env.VITE_ADMIN_API_URL);
